import { useMemo, useRef, useState } from "react";
import {
	MAX_TRIP_EXPENSES,
	parseExpenseCsv,
	previewExpenseRows,
	tripParty,
	type ExpenseColumnMap,
	type TripWorkspace,
} from "@japangolmok/core";
import type { PersistTrip } from "./TripExperience";

export function ExpenseImport({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [rows, setRows] = useState<string[][]>([]);
	const [map, setMap] = useState<ExpenseColumnMap>({
		date: -1,
		title: -1,
		amount: -1,
		currency: null,
	});
	const [currency, setCurrency] = useState<"" | "JPY" | "KRW">("");
	const [payer, setPayer] = useState("");
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	const saving = useRef(false);
	const readGeneration = useRef(0);
	const read = async (file?: File) => {
		const token = ++readGeneration.current;
		setRows([]);
		setMap({ date: -1, title: -1, amount: -1, currency: null });
		setPayer("");
		setCurrency("");
		setError("");
		setNotice("");
		if (!file) return;
		try {
			if (file.size > 2_000_000) throw new Error("size");
			const bytes = await file.arrayBuffer();
			let text: string;
			try {
				text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
			} catch {
				text = new TextDecoder("euc-kr", { fatal: true }).decode(bytes);
			}
			const result = parseExpenseCsv(text);
			if (result.length < 2) throw new Error("empty");
			if (token === readGeneration.current) setRows(result);
		} catch {
			if (token === readGeneration.current)
				setError(
					"CSV를 읽지 못했어요. 2MB 이하의 UTF-8 또는 CP949 CSV인지 확인해 주세요. 원본 파일은 그대로예요.",
				);
		}
	};
	const preview = rows.length
		? previewExpenseRows(rows.slice(1), map, currency || "JPY")
		: [];
	const headers = useMemo(
		() =>
			(rows[0] ?? []).map((name, index) => ({
				name,
				index,
				id: crypto.randomUUID(),
			})),
		[rows],
	);
	const remaining = Math.max(0, MAX_TRIP_EXPENSES - trip.expenses.length);
	const tooMany = preview.length > remaining;
	const invalid =
		tooMany ||
		(map.currency === null && !currency) ||
		[map.date, map.title, map.amount].some(
			(index) => index < 0 || index >= headers.length,
		) ||
		new Set([
			map.date,
			map.title,
			map.amount,
			...(map.currency === null ? [] : [map.currency]),
		]).size !==
			3 + (map.currency === null ? 0 : 1) ||
		!payer ||
		preview.some((r) => r.error || !trip.days.some((d) => d.date === r.date));

	const choose = (label: string, key: "date" | "title" | "amount") => (
		<label>
			{label}
			<select
				value={map[key]}
				onChange={(e) => setMap({ ...map, [key]: Number(e.target.value) })}
			>
				<option value={-1}>열을 선택해 주세요</option>
				{headers.map((h) => (
					<option key={h.id} value={h.index}>
						{h.index + 1}열 · {h.name || "(제목 없음)"}
					</option>
				))}
			</select>
		</label>
	);
	const save = async () => {
		if (saving.current || !preview.length || invalid) return;
		saving.current = true;
		setBusy(true);
		setError("");
		try {
			const party = tripParty(trip);
			const ok = await persist((t) => {
				for (const r of preview)
					t.expenses.push({
						id: crypto.randomUUID(),
						title: r.title,
						category: "other",
						occurredOn: r.date,
						currency: r.currency!,
						amountJpy: r.currency === "JPY" ? r.amount : 1,
						amountKrw: r.currency === "KRW" ? r.amount : undefined,
						paidBy: payer,
						splitWith: party.map((p) => p.id),
					});
			});
			if (ok !== false) {
				setNotice(
					`${preview.length}건을 가져왔어요. 낸 사람과 나눌 사람은 각 지출에서 확인해 주세요.`,
				);
				setRows([]);
			} else
				setError("저장하지 못했어요. 선택한 파일과 기존 지출은 그대로예요.");
		} catch {
			setError("저장하지 못했어요. 저장 공간을 확인한 뒤 다시 시도해 주세요.");
		} finally {
			saving.current = false;
			setBusy(false);
		}
	};
	return (
		<details className="editor-panel">
			<summary>다른 앱 가계부 가져오기</summary>
			<p>
				CSV 파일은 이 기기에서만 읽어요. 첫 행을 열 이름으로 보고, 아래에서
				날짜·내용·금액을 직접 맞춰 주세요.
			</p>
			<label className="file-button button secondary">
				CSV 파일 고르기
				<input
					className="sr-only"
					type="file"
					accept=".csv,text/csv"
					disabled={busy}
					onChange={(e) => {
						void read(e.currentTarget.files?.[0]);
						e.currentTarget.value = "";
					}}
				/>
			</label>
			{error && <p role="alert">{error}</p>}
			{notice && <p role="status">{notice}</p>}
			{!!rows.length && (
				<>
					<div className="form-grid">
						{choose("날짜 열", "date")}
						{choose("내용 열", "title")}
						{choose("금액 열", "amount")}
						<label>
							통화 열
							<select
								value={map.currency ?? ""}
								onChange={(e) =>
									setMap({
										...map,
										currency:
											e.target.value === "" ? null : Number(e.target.value),
									})
								}
							>
								<option value="">모두 같은 통화</option>
								{headers.map((h) => (
									<option key={h.id} value={h.index}>
										{h.index + 1}열 · {h.name || "(제목 없음)"}
									</option>
								))}
							</select>
						</label>
						{map.currency === null && (
							<label>
								통화
								<select
									value={currency}
									onChange={(e) =>
										setCurrency(e.target.value as typeof currency)
									}
								>
									<option value="">통화를 선택해 주세요</option>
									<option value="JPY">엔</option>
									<option value="KRW">원</option>
								</select>
							</label>
						)}
					</div>
					<label>
						이 파일의 지출을 낸 사람
						<select value={payer} onChange={(e) => setPayer(e.target.value)}>
							<option value="">직접 선택해 주세요</option>
							{tripParty(trip).map((p) => (
								<option key={p.id} value={p.id}>
									{p.nickname}
								</option>
							))}
						</select>
					</label>
					<p>
						모든 행을 선택한 사람이 내고 일행 모두가 나눈 것으로 가져와요. 서로
						다른 지출은 가져온 뒤 각 항목을 고쳐 주세요. 날짜는 연도 포함
						YYYY-MM-DD 형식이며 여행 기간 안이어야 해요.
					</p>
					<h3>가져올 내용 {preview.length}건</h3>
					{tooMany && <p role="alert">한 여행에는 지출을 최대 {MAX_TRIP_EXPENSES.toLocaleString()}건까지 저장할 수 있어요. 기존 {trip.expenses.length}건을 제외하면 {remaining}건을 더 가져올 수 있어요. 다른 여행으로 나누거나 기존 지출을 정리해 주세요.</p>}
					<ol>
						{preview.map((r) => (
							<li key={r.index}>
								{r.index}행 · {r.date || "날짜 없음"} · {r.title || "내용 없음"}{" "}
								·{" "}
								{Number.isFinite(r.amount)
									? r.amount.toLocaleString()
									: "금액 확인"}{" "}
								{map.currency === null && !currency
									? "통화 선택"
									: (r.currency ?? "통화 확인")}{" "}
								{r.error ||
									(!trip.days.some((d) => d.date === r.date)
										? "여행 기간 밖 날짜예요"
										: "")}
							</li>
						))}
					</ol>
					{invalid && !tooMany && (
						<p role="alert">
							확인이 필요한 행이 있어요. 열을 다시 맞추거나 원본 CSV를 수정해
							다시 골라 주세요.
						</p>
					)}
					<div className="button-row">
						<button
							type="button"
							className="button primary"
							disabled={busy || invalid || !preview.length}
							onClick={() => void save()}
						>
							{busy ? "저장 중" : "확인하고 가져오기"}
						</button>
						<button
							type="button"
							className="button secondary"
							disabled={busy}
							onClick={() => {
								++readGeneration.current;
								setRows([]);
							}}
						>
							취소
						</button>
					</div>
				</>
			)}
		</details>
	);
}
